import { useState, useMemo, useCallback } from "react";
import {
  View,
  Text,
  ScrollView,
  Pressable,
  Platform,
  Image,
} from "react-native";
import { ChevronLeft } from "lucide-react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import {
  getBlocksByKind,
  type ContentBlock,
  type MedicationBody,
} from "@src/packages/content";
import { useTranslations, useLocale } from "@src/packages/i18n";
import { EMBER } from "../../../onboarding/theme";
import type { ChatStackParamList } from "../../types";
import { MoonFilterChip, MoonSearchInput } from "@louise/moon-ui";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import { SCREEN_PADDING_TOP } from "@louise/moon-ui/theme/tokens";
import { useSmartBack, getBackLabel } from "../../hooks/useSmartBack";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

const pillCapsuleImage =
  require("../../../assets/images/pill-capsule.png") as number;
const vialSyringeImage =
  require("../../../assets/images/vial-syringe.png") as number;
const injectionPenImage =
  require("../../../assets/images/injection-pen.png") as number;

function getMedImage(body: MedicationBody): number {
  const forms = body.commonForms;
  if (forms.includes("pen")) return injectionPenImage;
  if (forms.includes("vial")) return vialSyringeImage;
  return pillCapsuleImage;
}

/* ─── Filter config ────────────────────────────────────────────── */

// Maps the raw drug-class value coming from CMS to a stable i18n key.
const DRUG_CLASS_I18N_KEYS: Record<string, string> = {
  Gonadotropin: "gonadotropin",
  "GnRH Antagonist": "gnrh_antagonist",
  "GnRH Agonist": "gnrh_agonist",
  "Trigger Shot": "trigger_shot",
  Progesterone: "progesterone",
  Estrogen: "estrogen",
  "Oral Ovulation Induction": "oral_ovulation_induction",
  Anticoagulant: "anticoagulant",
  Supplement: "supplement",
};

type Props = NativeStackScreenProps<ChatStackParamList, "Medications">;

export default function MedicationsScreen({ route, navigation }: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("MedicationsScreen");
  const returnTab = route.params?.returnTab;
  const handleBack = useSmartBack({
    returnTab,
    fallbackTab: "Learn",
    fallbackScreen: "Learn",
  });
  const backLabel =
    returnTab && returnTab !== "Learn"
      ? getBackLabel(returnTab, "Learn", t("you.shared.back"))
      : t("learn.back_to_learn");
  const { locale } = useLocale();
  const [search, setSearch] = useState("");
  const [activeClass, setActiveClass] = useState<string | null>(null);

  const medications = useMemo(() => getBlocksByKind("medication"), []);

  // Reads body.frBody when active locale is FR — falls back to EN otherwise.
  // The MedicationFactsheetSheet detail view already does this; the
  // encyclopedia card list needs the same treatment for genericName,
  // drugClass, and purpose so cards aren't mixed-language.
  const fieldsFor = useCallback(
    (body: MedicationBody) => {
      const fr = locale === "fr" ? body.frBody : undefined;
      return {
        genericName: fr?.genericName ?? body.genericName,
        drugClass: fr?.drugClass ?? body.drugClass,
        purpose: fr?.purpose ?? body.purpose,
      };
    },
    [locale],
  );

  // Derive available drug classes from data
  const drugClasses = useMemo(() => {
    const classes = new Set<string>();
    for (const med of medications) {
      if (med.body.drugClass) classes.add(med.body.drugClass);
    }
    return Array.from(classes).sort();
  }, [medications]);

  const filtered = useMemo(() => {
    let result = medications;

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter((m) => {
        const f = fieldsFor(m.body);
        return (
          m.title.toLowerCase().includes(q) ||
          f.genericName.toLowerCase().includes(q) ||
          f.drugClass.toLowerCase().includes(q) ||
          f.purpose.toLowerCase().includes(q) ||
          m.body.brandsByCountry?.some((entry) =>
            entry.brands.some((b) => b.toLowerCase().includes(q)),
          )
        );
      });
    }

    if (activeClass) {
      // activeClass is always the EN drugClass (the filter chips use raw EN
      // values as identity); match against base body.drugClass not the
      // locale-aware version.
      result = result.filter((m) => m.body.drugClass === activeClass);
    }

    result.sort((a, b) => a.title.localeCompare(b.title));
    return result;
  }, [medications, search, activeClass, fieldsFor]);

  // Navigate to the deep-linkable factsheet route so the URL reflects the
  // open medication (shareable / back-button friendly).
  const onItemPress = useCallback(
    (item: ContentBlock<"medication">) => {
      capturePress(pressLabel(item.title), "learn_medication_card");
      navigation.navigate("MedicationDetail", { medicationId: item.id });
    },
    [capturePress, navigation],
  );

  // Pair items into rows of 2
  const rows = useMemo(() => {
    const result: ContentBlock<"medication">[][] = [];
    for (let i = 0; i < filtered.length; i += 2) {
      result.push(filtered.slice(i, i + 2));
    }
    return result;
  }, [filtered]);

  return (
    <GlassScreenLayout>
      <ScrollView
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP,
          paddingBottom: 100,
          maxWidth: 520,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        {/* Back button */}
        <Pressable
          onPress={() => {
            // backLabel is translated and varies with the origin tab.
            capturePress(pressLabel(backLabel), "learn_encyclopedia_back");
            handleBack();
          }}
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 4,
            marginBottom: 8,
          }}
        >
          <ChevronLeft size={18} color={EMBER.colors.warm} />
          <Text
            className="font-dm-sans"
            style={{ fontSize: 14, color: EMBER.colors.warm }}
          >
            {backLabel}
          </Text>
        </Pressable>

        {/* Title */}
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 38,
            fontWeight: "700",
            color: "#3B1A08",
            lineHeight: 42,
            letterSpacing: -0.5,
            ...(Platform.OS === "web" && {
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.7' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23n)' opacity='0.06'/%3E%3C/svg%3E"), linear-gradient(135deg, #3B1A08 0%, #7A3E15 35%, #3B1A08 60%, #6B3410 85%, #3B1A08 100%)`,
              backgroundSize: "200px 200px, 100% 100%",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }),
          }}
        >
          {t("learn.encyclopedia_screens.medications.title")}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 15,
            color: EMBER.colors.textLight,
            marginTop: 2,
            lineHeight: 21,
          }}
        >
          {t("learn.encyclopedia_screens.medications.subtitle")}
        </Text>

        {/* Search bar */}
        <MoonSearchInput
          value={search}
          onChangeText={setSearch}
          placeholder={t(
            "learn.encyclopedia_screens.medications.search_placeholder",
          )}
          onClear={() => setSearch("")}
          tone="glass"
          style={{ marginTop: 18 }}
        />

        {/* Drug class filter chips */}
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{
            gap: 8,
            paddingVertical: 14,
          }}
        >
          <MoonFilterChip
            label={t("learn.encyclopedia_screens.all")}
            selected={!activeClass}
            onPress={() => setActiveClass(null)}
            tone="glass"
            showCheck={false}
          />
          {drugClasses.map((dc) => {
            const isActive = activeClass === dc;
            return (
              <MoonFilterChip
                key={dc}
                label={
                  DRUG_CLASS_I18N_KEYS[dc]
                    ? t(
                        `learn.encyclopedia_screens.medications.drug_classes.${DRUG_CLASS_I18N_KEYS[dc]}`,
                      )
                    : dc
                }
                selected={isActive}
                onPress={() => setActiveClass(isActive ? null : dc)}
                tone="glass"
                showCheck={false}
              />
            );
          })}
        </ScrollView>

        {/* Result count */}
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            color: EMBER.colors.textFaint,
            marginBottom: 14,
          }}
        >
          {t(
            filtered.length === 1
              ? "learn.encyclopedia_screens.medications.count_one"
              : "learn.encyclopedia_screens.medications.count_other",
            { params: { count: String(filtered.length) } },
          )}
        </Text>

        {/* 2x2 Grid */}
        <View style={{ gap: 12 }}>
          {rows.map((row, rowIdx) => (
            <View key={rowIdx} style={{ flexDirection: "row", gap: 12 }}>
              {row.map((med) => {
                const { body } = med;
                const firstRoute = body.commonRoutes[0];
                return (
                  <Pressable
                    key={med.id}
                    onPress={() => onItemPress(med)}
                    style={{
                      flex: 1,
                      borderRadius: 22,
                      overflow: "hidden",
                      backgroundColor: "rgba(255, 255, 255, 0.4)",
                      borderWidth: 1,
                      borderColor: "rgba(255, 255, 255, 0.6)",
                      shadowColor: "#000",
                      shadowOffset: { width: 0, height: 4 },
                      shadowOpacity: 0.04,
                      shadowRadius: 16,
                      ...(Platform.OS === "web" &&
                        ({
                          backdropFilter: "blur(40px)",
                          WebkitBackdropFilter: "blur(40px)",
                        } as Record<string, string>)),
                    }}
                  >
                    {/* Icon */}
                    <View
                      style={{
                        alignItems: "center",
                        paddingTop: 24,
                        paddingBottom: 12,
                      }}
                    >
                      <Image
                        source={getMedImage(body)}
                        style={{ width: 52, height: 52 }}
                        resizeMode="contain"
                      />
                    </View>

                    {/* Content */}
                    <View
                      style={{
                        padding: 16,
                        paddingTop: 4,
                        gap: 6,
                        alignItems: "center",
                      }}
                    >
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 15,
                          fontWeight: "700",
                          color: EMBER.colors.text,
                          lineHeight: 20,
                          textAlign: "center",
                        }}
                        numberOfLines={2}
                      >
                        {med.title}
                      </Text>

                      {fieldsFor(body).genericName ? (
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 12,
                            color: EMBER.colors.textLight,
                            lineHeight: 17,
                            textAlign: "center",
                          }}
                          numberOfLines={1}
                        >
                          {fieldsFor(body).genericName}
                        </Text>
                      ) : null}

                      {/* Tags */}
                      <View
                        style={{
                          flexDirection: "row",
                          flexWrap: "wrap",
                          justifyContent: "center",
                          gap: 4,
                          marginTop: 4,
                        }}
                      >
                        <View
                          style={{
                            paddingHorizontal: 8,
                            paddingVertical: 4,
                            borderRadius: 8,
                            backgroundColor: "rgba(0,0,0,0.03)",
                            borderWidth: 1,
                            borderColor: "rgba(0,0,0,0.05)",
                          }}
                        >
                          <Text
                            className="font-dm-sans"
                            style={{
                              fontSize: 10,
                              fontWeight: "500",
                              color: EMBER.colors.textMid,
                              letterSpacing: 0.2,
                            }}
                          >
                            {firstRoute
                              ? t(
                                  `learn.encyclopedia_screens.medications.routes.${firstRoute}`,
                                )
                              : fieldsFor(body).drugClass}
                          </Text>
                        </View>
                      </View>
                    </View>
                  </Pressable>
                );
              })}
              {row.length === 1 && <View style={{ flex: 1 }} />}
            </View>
          ))}
        </View>

        {/* Empty state */}
        {filtered.length === 0 && (
          <View style={{ alignItems: "center", paddingTop: 40 }}>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 15,
                color: EMBER.colors.textLight,
                textAlign: "center",
              }}
            >
              {t("learn.encyclopedia_screens.medications.empty")}
            </Text>
          </View>
        )}
      </ScrollView>
    </GlassScreenLayout>
  );
}
